import moment from "moment";
import { Dispatch, SetStateAction, useCallback, useEffect } from "react";
import { LocalStorageKeyEnum } from "../../enums/localStorage";
import {
  AuthInfoList,
  IDecryptedAuthInfo,
  IEncryptedAuthInfo,
} from "../../interfaces/AuthInfo";
import { AuthInfoMap, AuthInfoType } from "./types";
import { getAccountKey, getAuthInfoStorageKey } from "./helpers";

interface IUseAuthActionsExtraParams {
  decryptedAccountAuthInfo:
    | IDecryptedAuthInfo
    | IEncryptedAuthInfo
    | undefined;
  authInfoList: AuthInfoList;
  storeAuthInfoToLocalStorage: (info: AuthInfoList) => void;
  setAuthInfoList: Dispatch<SetStateAction<AuthInfoList>>;
  setLocalPassword: Dispatch<SetStateAction<string | undefined>>;
  setImportedAccount: Dispatch<SetStateAction<string | undefined>>;
  importedAccountAddress: string | undefined;
  isGeoblocked: boolean | undefined;
  deactivate: () => Promise<void>;
}

export function useAuthActionsExtra({
  decryptedAccountAuthInfo,
  authInfoList,
  storeAuthInfoToLocalStorage,
  setAuthInfoList,
  setLocalPassword,
  setImportedAccount,
  importedAccountAddress,
  isGeoblocked,
  deactivate,
}: IUseAuthActionsExtraParams) {
  const disconnectAccount = useCallback(async () => {
    await deactivate();
    setImportedAccount(undefined);
    setLocalPassword(undefined);
  }, [deactivate]);

  const deleteAuthInfo = useCallback(
    (type: AuthInfoType) => {
      if (decryptedAccountAuthInfo) {
        const accountKey = getAccountKey(decryptedAccountAuthInfo.account);
        if (accountKey) {
          const updatedAuthInfoList: AuthInfoMap = {
            ...((authInfoList || {}) as AuthInfoMap),
          };
          const matchedEntry = Object.entries(updatedAuthInfoList).find(
            ([key, info]) =>
              key.toLowerCase() === accountKey ||
              getAccountKey(info.account) === accountKey
          );
          const storedKey = matchedEntry?.[0];
          const accountInfo = matchedEntry?.[1] as
            | IDecryptedAuthInfo
            | undefined;

          if (accountInfo) {
            if (storedKey && storedKey !== accountKey) {
              delete updatedAuthInfoList[storedKey];
            }

            switch (type) {
              case "api":
                if (accountInfo.apiKey && accountInfo.apiSecret) {
                  delete accountInfo.apiKey;
                  delete accountInfo.apiSecret;
                  updatedAuthInfoList[accountKey] = accountInfo;
                }
                break;
              case "signing":
                if (accountInfo.signingKey || accountInfo.expiry) {
                  delete accountInfo.signingKey;
                  delete accountInfo.expiry;
                  updatedAuthInfoList[accountKey] = accountInfo;
                }
                break;
              case "trading": {
                // One write for all three (H2): two back-to-back deleteAuthInfo
                // calls read the same stale list, and the second would restore
                // what the first removed.
                const {
                  apiKey: _apiKey,
                  apiSecret: _apiSecret,
                  signingKey: _signingKey,
                  expiry: _expiry,
                  ...rest
                } = accountInfo;
                updatedAuthInfoList[accountKey] = rest as IDecryptedAuthInfo;
                break;
              }
              case "all":
                delete updatedAuthInfoList[accountKey];
                break;
              default:
                // handle default case if necessary
                break;
            }

            setAuthInfoList({ ...updatedAuthInfoList } as AuthInfoList);
            storeAuthInfoToLocalStorage(updatedAuthInfoList as AuthInfoList);
            sessionStorage.setItem(
              getAuthInfoStorageKey(),
              JSON.stringify(updatedAuthInfoList)
            );
          }
        }
      }

      if (importedAccountAddress) {
        setImportedAccount(undefined);
        window.localStorage.removeItem(
          LocalStorageKeyEnum.IMPORTED_ACCOUNT_ADDRESS
        );
      }
    },
    [
      decryptedAccountAuthInfo,
      importedAccountAddress,
      authInfoList,
      storeAuthInfoToLocalStorage,
    ]
  );

  // Checks for signing key expiry in 1 minute intervals, or when geoblocked
  useEffect(() => {
    const exp = decryptedAccountAuthInfo?.encrypted
      ? undefined
      : decryptedAccountAuthInfo?.expiry;
    if (exp) {
      const checkExpiry = () => {
        const hasExpired = moment().unix() >= Number(exp);
        if (hasExpired || isGeoblocked) deleteAuthInfo("signing");
      };
      // Check Expiry once, then repeat every minute
      checkExpiry();
      const authChecker = setInterval(checkExpiry, 1000 * 60);

      return () => clearInterval(authChecker);
    }

    return () => {};
  }, [decryptedAccountAuthInfo, deleteAuthInfo, isGeoblocked]);

  return {
    disconnectAccount,
    deleteAuthInfo,
  };
}
